import React from 'react'
import SbEditable from 'storyblok-react'
import clsx from 'clsx'
import { SEO } from '@/components/atoms/SEO/SEO'
import { Header } from '@/components/organisms/Header/Header'
import { HeaderSearch } from '@/components/organisms/HeaderSearch/HeaderSearch'
import { Footer } from '@/components/organisms/Footer/Footer'
import { DynamicComponent } from '@/utils/DynamicComponent/DynamicComponent'
import useStoryblok from '@/hooks/useStoryblok'
import { SEOProps } from '@/components/atoms/SEO/types'
import type { MainType } from './Main.types'
import 'what-input'

export function Main({ pageContext, location, pageContent }: MainType) {
  const layoutStaticData = pageContext ? JSON.parse(pageContext.layout?.content || '{}') : pageContent.layout
  const story = pageContext ? useStoryblok(pageContext.blok, location) : pageContent.blok
  const { content } = story
  const currentLang = pageContext?.currentLang || pageContent?.currentLang

  const contentMap =
    content.body &&
    content.body.map((childBlok: any) => <DynamicComponent blok={childBlok} location={location} key={childBlok._uid} />)

  const checkHeaderType = (componentName: string) => componentName === 'partial_header_simple'

  const {
    seo_title,
    seo_description,
    set_noarchive,
    set_nofollow,
    set_noindex,
    canonical,
    og_title,
    og_description,
    og_image,
    og_locale,
    og_type,
    og_url,
    twitter_card,
    twitter_creator,
    show_logo_only,
    schema_markup,
    keywords
  } = content

  const alternates = pageContext?.blok.alternates || pageContent?.blok.alternates

  const seoData: SEOProps = {
    title: story.name,
    seo_title,
    seo_description,
    set_noarchive,
    set_nofollow,
    set_noindex,
    canonical,
    og_title,
    og_description,
    og_image,
    og_locale,
    og_type,
    og_url,
    twitter_card,
    twitter_creator,
    alternates,
    schema_markup,
    lang: currentLang?.code,
    page_slug: pageContext?.url,
    branch: pageContext?.branch,
    keywords,
    location
  }

  if (story == null) {
    return (
      <div>
        <h3>Editor page</h3>
      </div>
    )
  }

  const finalContent = content.component === 'template_layout' ? content : layoutStaticData
  const headerData = finalContent.header && finalContent.header[0]
  const footerData = finalContent.footer && finalContent.footer[0]
  const headerComponent =
    headerData.component === 'partial_header_search' ? (
      <HeaderSearch {...headerData} currentLang={currentLang} />
    ) : (
      <Header
        {...headerData}
        currentLang={currentLang}
        languages={pageContext?.languages || pageContent?.languages}
        simple={checkHeaderType(headerData.component)}
        showLogoOnly={show_logo_only}
        alternates={story.alternates}
      />
    )

  return (
    // @ts-ignore
    <SbEditable content={content}>
      <SEO {...seoData} />
      {/* @ts-ignore */}
      <SbEditable content={headerData} key={headerData?._uid}>
        {headerData && headerComponent}
      </SbEditable>
      {contentMap || <h3>Layout component</h3>}
      {/* @ts-ignore */}
      <SbEditable content={footerData} key={footerData?._uid}>
        {footerData && (
          <Footer
            {...footerData}
            currentLang={currentLang}
            languages={pageContext?.languages || pageContent?.languages}
            alternates={story.alternates}
            className={clsx(story.slug === 'help' ? 'max-w-90 px-24 laptop:px-90 desktop:px-0' : '')}
          />
        )}
      </SbEditable>
    </SbEditable>
  )
}

export default Main
